'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Key, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';

export default function MeetingAttendanceChangePasswordPage() {
  const router = useRouter();
  const { isReady, user } = useAuthGuard();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (passwordData.newPassword.length < 6) {
      toast.error(t.meetingAttendance.changePassword.passwordTooShort);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t.meetingAttendance.changePassword.passwordMismatch);
      return;
    }

    if (!user?.id) {
      toast.error(t.meetingAttendance.changePassword.userNotFound);
      return;
    }

    setLoading(true);
    try {
      await meetingAttendanceApi.resetMeetingUserPassword(user.id, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      toast.success(t.meetingAttendance.changePassword.updateSuccess, {
        description: t.meetingAttendance.changePassword.updateSuccessDesc,
        duration: 4000,
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      const message = error?.response?.data?.error || t.meetingAttendance.changePassword.updateFailed;
      toast.error(message, {
        description: t.meetingAttendance.changePassword.updateFailedDesc,
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">{t.meetingAttendance.common.loading}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => router.back()} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.meetingAttendance.common.back}
        </Button>
        <h1 className="text-3xl font-bold">{t.meetingAttendance.changePassword.title}</h1>
        <p className="text-muted-foreground mt-2">
          {t.meetingAttendance.changePassword.subtitle}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            {t.meetingAttendance.changePassword.formTitle}
          </CardTitle>
          <CardDescription>
            {t.meetingAttendance.changePassword.formDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">{t.meetingAttendance.changePassword.currentPassword}</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(event) => setPasswordData({ ...passwordData, currentPassword: event.target.value })}
                placeholder={t.meetingAttendance.changePassword.currentPasswordPlaceholder}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="newPassword">{t.meetingAttendance.changePassword.newPassword}</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(event) => setPasswordData({ ...passwordData, newPassword: event.target.value })}
                placeholder={t.meetingAttendance.changePassword.newPasswordPlaceholder}
                required
                minLength={6}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">{t.meetingAttendance.changePassword.passwordHint}</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">{t.meetingAttendance.changePassword.confirmPassword}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(event) => setPasswordData({ ...passwordData, confirmPassword: event.target.value })}
                placeholder={t.meetingAttendance.changePassword.confirmPasswordPlaceholder}
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            {passwordData.newPassword && passwordData.confirmPassword &&
             passwordData.newPassword !== passwordData.confirmPassword && (
              <p className="text-sm text-red-600">{t.meetingAttendance.changePassword.passwordMismatch}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => router.back()}>
                {t.meetingAttendance.changePassword.cancelAction}
              </Button>
              <Button
                type="submit"
                disabled={
                  loading ||
                  !passwordData.currentPassword ||
                  !passwordData.newPassword ||
                  !passwordData.confirmPassword ||
                  passwordData.newPassword !== passwordData.confirmPassword ||
                  passwordData.newPassword.length < 6
                }
              >
                {loading ? t.meetingAttendance.changePassword.updating : t.meetingAttendance.changePassword.updateAction}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
